import React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from "./ui/button";
import { Menu, X, Globe } from 'lucide-react';
import { toast } from "sonner";
import logo from "../assets/logo.png";

const Layout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const { lang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  // Helper to get current path without lang prefix
  const getPathWithoutLang = () => {
    const pathParts = location.pathname.split('/');
    // pathParts[0] is empty, pathParts[1] is lang
    return '/' + pathParts.slice(2).join('/');
  };

  const isActive = (path) => {
    const currentPath = getPathWithoutLang();
    // Handle root case where path is just '/'
    if (path === '/' && currentPath === '/') return true;
    return currentPath === path;
  };

  const handleDemoClick = (action) => {
    toast.info(`${action} functionality is coming soon!`, {
      description: "This is a showcase demo site."
    });
  };

  const toggleLanguage = () => {
    let newLang = 'en';
    if (lang === 'en') newLang = 'ja';
    else if (lang === 'ja') newLang = 'fr';
    else if (lang === 'fr') newLang = 'en';

    const currentPath = getPathWithoutLang();
    // If currentPath is just '/', we need to make sure we don't end up with //
    const targetPath = currentPath === '/' ? '' : currentPath;
    navigate(`/${newLang}${targetPath}`);
    
    let message = "Switched to English";
    if (newLang === 'ja') message = "日本語に切り替えました";
    if (newLang === 'fr') message = "Passé en Français";

    toast.info(message, {
      description: "Localization updated"
    });
  };

  return (
    <div className="min-h-screen flex flex-col font-sans text-slate-900 bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 w-full border-b border-slate-100 bg-white/80 backdrop-blur-md">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-2">
              <Link to={`/${lang}`} className="flex items-center gap-2">
                <img 
                  src={logo} 
                  alt="Internjin Logo" 
                  className="h-10 w-auto object-contain"
                />
              </Link>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <Link 
                to={`/${lang}`} 
                className={`text-sm font-medium transition-colors hover:text-red-600 ${isActive('/') ? 'text-red-600' : 'text-slate-600'}`}
              >
                {lang === 'ja' ? 'ホーム' : lang === 'fr' ? 'Accueil' : 'Home'}
              </Link>
              <Link 
                to={`/${lang}/contact`} 
                className={`text-sm font-medium transition-colors hover:text-red-600 ${isActive('/contact') ? 'text-red-600' : 'text-slate-600'}`}
              >
                {lang === 'ja' ? 'お問い合わせ' : lang === 'fr' ? 'Contact' : 'Contact Us'}
              </Link>
              <Link 
                to={`/${lang}/faq`} 
                className={`text-sm font-medium transition-colors hover:text-red-600 ${isActive('/faq') ? 'text-red-600' : 'text-slate-600'}`}
              >
                {lang === 'ja' ? 'よくある質問' : 'FAQ'}
              </Link>
              <Link 
                to={`/${lang}/testimonials`} 
                className={`text-sm font-medium transition-colors hover:text-red-600 ${isActive('/testimonials') ? 'text-red-600' : 'text-slate-600'}`}
              >
                {lang === 'ja' ? '体験談' : lang === 'fr' ? 'Témoignages' : 'Testimonials'}
              </Link>
              
              <div className="h-4 w-px bg-slate-200 mx-2"></div>
              
              <button 
                onClick={toggleLanguage}
                className="text-sm font-medium text-slate-600 hover:text-red-600 transition-colors flex items-center gap-1.5"
              >
                <Globe className="h-4 w-4" />
                <span className="uppercase">{lang}</span>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button className="md:hidden p-2" onClick={toggleMenu}>
              {isMenuOpen ? <X className="h-6 w-6 text-slate-600" /> : <Menu className="h-6 w-6 text-slate-600" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-slate-100 bg-white p-4 space-y-4">
            <Link 
              to={`/${lang}`} 
              className="block text-sm font-medium text-slate-600 hover:text-red-600"
              onClick={() => setIsMenuOpen(false)}
            >
              {lang === 'ja' ? 'ホーム' : lang === 'fr' ? 'Accueil' : 'Home'}
            </Link>
            <Link 
              to={`/${lang}/contact`} 
              className="block text-sm font-medium text-slate-600 hover:text-red-600"
              onClick={() => setIsMenuOpen(false)}
            >
              {lang === 'ja' ? 'お問い合わせ' : lang === 'fr' ? 'Contact' : 'Contact Us'}
            </Link>
            <Link 
              to={`/${lang}/faq`} 
              className="block text-sm font-medium text-slate-600 hover:text-red-600"
              onClick={() => setIsMenuOpen(false)}
            >
              {lang === 'ja' ? 'よくある質問' : 'FAQ'}
            </Link>
            <button 
              onClick={() => {
                toggleLanguage();
                setIsMenuOpen(false);
              }}
              className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-red-600 w-full py-2 text-left"
            >
              <Globe className="h-4 w-4" />
              {lang === 'en' ? 'Switch to Japanese' : lang === 'ja' ? 'Passer en Français' : 'Switch to English'}
            </button>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-12 border-t border-slate-800">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                 <img 
                  src={logo} 
                  alt="Internjin Logo" 
                  className="h-8 w-auto"
                />
              </div>
              <p className="text-sm text-slate-400 max-w-xs">
                "Empowering Interns. Elevating Employers."
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-4">Platform</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to={`/${lang}`} className="hover:text-red-400 transition-colors">{lang === 'ja' ? 'ホーム' : lang === 'fr' ? 'Accueil' : 'Home'}</Link></li>
                <li><Link to={`/${lang}/faq`} className="hover:text-red-400 transition-colors">{lang === 'ja' ? 'よくある質問' : 'FAQ'}</Link></li>
                <li><Link to={`/${lang}/testimonials`} className="hover:text-red-400 transition-colors">{lang === 'ja' ? '体験談' : lang === 'fr' ? 'Témoignages' : 'Testimonials'}</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-4">Company</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to={`/${lang}/about`} className="hover:text-red-400 transition-colors">{lang === 'ja' ? '会社概要' : lang === 'fr' ? 'À Propos' : 'About Us'}</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-slate-800 text-center text-sm text-slate-500">
            © {new Date().getFullYear()} Internjin (Pre-seed Startup). All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
